import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Mode, applyMode, Density, applyDensity } from '@cloudscape-design/global-styles';

interface ThemeContextType {
  mode: Mode;
  density: Density;
  setMode: (mode: Mode) => void;
  setDensity: (density: Density) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or default values
  const [mode, setModeState] = useState<Mode>(() => {
    const savedMode = localStorage.getItem('cloudscape-mode');
    return (savedMode as Mode) || Mode.Light;
  });

  const [density, setDensityState] = useState<Density>(() => {
    const savedDensity = localStorage.getItem('cloudscape-density');
    return (savedDensity as Density) || Density.Comfortable;
  });

  // Apply theme changes and persist to localStorage
  const setMode = (newMode: Mode) => {
    setModeState(newMode);
    localStorage.setItem('cloudscape-mode', newMode);
    applyMode(newMode);
  };

  const setDensity = (newDensity: Density) => {
    setDensityState(newDensity);
    localStorage.setItem('cloudscape-density', newDensity);
    applyDensity(newDensity);
  };

  // Apply initial theme on mount
  useEffect(() => {
    applyMode(mode);
    applyDensity(density);
  }, [mode, density]);

  const contextValue: ThemeContextType = {
    mode,
    density,
    setMode,
    setDensity,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
